import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const iconButtonVariants = cva(
    [
        'inline-flex items-center justify-center shrink-0 rounded-xl',
        'transition-all duration-150 ease-in-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    ],
    {
        variants: {
            variant: {
                ghost: 'bg-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                outline: 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50',
                primary: 'bg-teal-600 text-white hover:bg-teal-500',
                destructive: 'bg-transparent text-red-400 hover:bg-red-50 hover:text-red-600',
            },
            size: {
                sm: 'w-7 h-7',
                md: 'w-9 h-9',
                lg: 'w-11 h-11',
            },
        },
        defaultVariants: {
            variant: 'ghost',
            size: 'md',
        },
    }
);

interface IconButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
    icon: ReactNode;
    /** Accessible label required for screen readers */
    label: string;
}

/**
 * IconButton — An icon-only action with a guaranteed square touch target.
 * Always requires an accessible `label` prop for screen reader support.
 *
 * @example
 * <IconButton icon={<Trash2 size={16} />} label="Delete item" variant="destructive" />
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ icon, label, variant, size, className, ...rest }, ref) => {
        return (
            <button
                ref={ref}
                aria-label={label}
                className={cn(iconButtonVariants({ variant, size }), className)}
                {...rest}
            >
                {icon}
            </button>
        );
    }
);

IconButton.displayName = 'IconButton';
